import get from 'lodash/get';

const NEED_CASE_PURPOSE = 'need_case';
const GLASS_CHECKLIST_PERFORMED_PURPOSE = 'glass_checklist_performed';

const sortedProjectFileEntries = (data) => {
  const files = get(data, 'projectFiles', []);
  if (!Array.isArray(files)) return [];
  return [...files].sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));
};

const firstUuidFromArray = (data, path) => {
  const arr = get(data, path, []);
  if (!Array.isArray(arr)) return '';
  const first = arr.find((u) => u != null && String(u).trim() !== '');
  return first != null ? String(first) : '';
};

const uuidsFromArray = (data, path) => {
  const arr = get(data, path, []);
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((u) => u != null && String(u).trim() !== '')
    .map((u) => String(u));
};

const fileDownloadInfoFromEntry = (entry) => {
  const file = entry?.file;
  if (!file) return null;
  const key =
    file.s3Key != null && String(file.s3Key).trim() !== ''
      ? String(file.s3Key)
      : '';
  const uuid =
    file.uuid != null && String(file.uuid).trim() !== ''
      ? String(file.uuid)
      : '';
  const name =
    (file.originalFilename && String(file.originalFilename)) ||
    (file.originalName && String(file.originalName)) ||
    (file.filename && String(file.filename)) ||
    '';
  if (!key && !uuid) return null;
  return { key, uuid, name };
};

const findNeedCaseFileDownloadInfo = (data) => {
  for (const entry of sortedProjectFileEntries(data)) {
    const purpose = String(entry?.purpose || '').toLowerCase();
    if (purpose === NEED_CASE_PURPOSE) {
      const info = fileDownloadInfoFromEntry(entry);
      if (info) return info;
    }
  }
  const fallbackUuid =
    firstUuidFromArray(data, 'needCaseFileUUIDs') ||
    firstUuidFromArray(data, 'project.needCaseFileUUIDs');
  if (fallbackUuid) {
    return { key: '', uuid: fallbackUuid, name: '' };
  }
  return null;
};

const findGlassChecklistPerformedFilesDownloadInfo = (data) => {
  const projectFiles = sortedProjectFileEntries(data)
    .filter(
      (entry) =>
        String(entry?.purpose || '').toLowerCase() ===
        GLASS_CHECKLIST_PERFORMED_PURPOSE,
    )
    .map(fileDownloadInfoFromEntry)
    .filter(Boolean);
  if (projectFiles.length) {
    return projectFiles;
  }

  const fallbackUuids = uuidsFromArray(
    data,
    'glassCheckListPerformedFileUUIDs',
  );
  if (fallbackUuids.length) {
    return fallbackUuids.map((uuid) => ({ key: '', uuid, name: '' }));
  }

  return uuidsFromArray(
    data,
    'glass.glassCheckListPerformedFileUUIDs',
  ).map((uuid) => ({ key: '', uuid, name: '' }));
};

/**
 * Need-case file from data.projectFiles (purpose need_case), or needCaseFileUUIDs.
 * Returns { key, uuid, name } or null. Stream download: GET /bulk/download-stream/{uuid}.
 */
export const getNeedCaseFileDownloadInfo = (data) =>
  findNeedCaseFileDownloadInfo(data);

/**
 * Glass checklist performed files from projectFiles (purpose glass_checklist_performed)
 * or glassCheckListPerformedFileUUIDs. Returns an ordered array of
 * { key, uuid, name } entries.
 * Stream download: GET /bulk/download-stream/{uuid}.
 */
export const getGlassChecklistPerformedFilesDownloadInfo = (data) =>
  findGlassChecklistPerformedFilesDownloadInfo(data);

/**
 * Backward-compatible accessor for callers that only support one glass checklist file.
 */
export const getGlassChecklistPerformedFileDownloadInfo = (data) =>
  findGlassChecklistPerformedFilesDownloadInfo(data)[0] || null;
